import { ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UiKitModule } from "@app/ui-kit.module";
import { GroupIdChipComponent } from "@components/group-number-chip/group-id-chip.component";
import { UtilityProductService } from "@domain/product/monitoring/utility-product.service";
import { ProductService } from "@domain/product/product.service";
import { AirPath } from "@domain/project/floorplan/air-path";
import { ProductTypesInGroup } from "@domain/project/floorplan/floorplan";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanFreeText } from "@domain/project/floorplan/floorplan-free-text";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanImage } from "@domain/project/floorplan/floorplan-image";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSelectedItemType } from "@domain/project/floorplan/floorplan-selected-item-type";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanSelectionMode } from "@domain/project/floorplan/floorplan-state";
import { FloorplanText } from "@domain/project/floorplan/floorplan-text";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { MeasurementLine } from "@domain/project/floorplan/measurement-line";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { ReferenceMeasure } from "@domain/project/floorplan/reference-measure";
import { CableRoute } from "@domain/project/floorplan/wiring/cable-route";
import { Pipeline } from "@domain/project/floorplan/wiring/pipeline";
import { ProtectiveTube } from "@domain/project/floorplan/wiring/protective-tube";
import { Wire } from "@domain/project/floorplan/wiring/wire";
import { DangerArea } from "@domain/project/floorplan/zones/danger-area";
import { DustExZone } from "@domain/project/floorplan/zones/dust-ex-zone";
import { ExZone } from "@domain/project/floorplan/zones/ex-zone";
import { IsolationRoom } from "@domain/project/floorplan/zones/isolation-room";
import { ItRoom } from "@domain/project/floorplan/zones/it-room";
import { OperatingTheatre } from "@domain/project/floorplan/zones/operating-theatre";
import { ProcedureRoom } from "@domain/project/floorplan/zones/procedure-room";
import { StandardRoom } from "@domain/project/floorplan/zones/standard-room";
import { WildcardZone } from "@domain/project/floorplan/zones/wildcard-zone";
import { ProjectImageService } from "@domain/project/project-image.service";
import { AirPathNotesComponent } from "@project/floorplanner/floorplan-additional-notes/air-path-notes/air-path-notes.component";
import { AlarmDeviceNotesComponent } from "@project/floorplanner/floorplan-additional-notes/alarm-device-notes/alarm-device-notes.component";
import { CableRouteNotesComponent } from "@project/floorplanner/floorplan-additional-notes/cable-route-notes/cable-route-notes.component";
import { FlameDetectorNotesComponent } from "@project/floorplanner/floorplan-additional-notes/flame-detector-notes/flame-detector-notes.component";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FreeTextNotesComponent } from "@project/floorplanner/floorplan-additional-notes/free-text-notes/free-text-notes.component";
import { GasWarningCenterNotesComponent } from "@project/floorplanner/floorplan-additional-notes/gas-warning-center-notes/gas-warning-center-notes.component";
import { ImageNotesComponent } from "@project/floorplanner/floorplan-additional-notes/image-notes/image-notes.component";
import { MeasurementLineNotesComponent } from "@project/floorplanner/floorplan-additional-notes/measurement-line-notes/measurement-line-notes.component";
import { CentralStationNotesComponent } from "@project/floorplanner/floorplan-additional-notes/monitoring/central-station-notes/central-station-notes.component";
import { KvmNotesComponent } from "@project/floorplanner/floorplan-additional-notes/monitoring/kvm-notes/kvm-notes.component";
import { PatientMonitorNotesComponent } from "@project/floorplanner/floorplan-additional-notes/monitoring/patient-monitor-notes/patient-monitor-notes.component";
import { UtilityNotesComponent } from "@project/floorplanner/floorplan-additional-notes/monitoring/utility-notes/utility-notes.component";
import { PipelineNotesComponent } from "@project/floorplanner/floorplan-additional-notes/pipeline-notes/pipeline-notes.component";
import { PlaceholderNotesComponent } from "@project/floorplanner/floorplan-additional-notes/placeholder-notes/placeholder-notes.component";
import { PlasticSignNotesComponent } from "@project/floorplanner/floorplan-additional-notes/plastic-sign-notes/plastic-sign-notes.component";
import { ProtectiveTubeNotesComponent } from "@project/floorplanner/floorplan-additional-notes/protective-tube-notes/protective-tube-notes.component";
import { ReferenceMeasureNotesComponent } from "@project/floorplanner/floorplan-additional-notes/reference-measure-notes/reference-measure-notes.component";
import { SignalNotesComponent } from "@project/floorplanner/floorplan-additional-notes/signal-notes/signal-notes.component";
import { TransmitterNotesComponent } from "@project/floorplanner/floorplan-additional-notes/transmitter-notes/transmitter-notes.component";
import { WireNotesComponent } from "@project/floorplanner/floorplan-additional-notes/wire-notes/wire-notes.component";
import { ZoneNotesComponent } from "@project/floorplanner/floorplan-additional-notes/zone-notes/zone-notes.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { ItemSelectIntention } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { map, Observable, of, switchMap, take, tap } from "rxjs";
import { DisplayNotesComponent } from "./monitoring/display-notes/display-notes.component";
import { TelemetryNotesComponent } from "./monitoring/telemetry-notes/telemetry-notes.component";

@Component({
  selector: "app-floorplan-additional-notes",
  templateUrl: "./floorplan-additional-notes.component.html",
  styleUrls: ["./floorplan-additional-notes.component.scss"],
  imports: [
    UiKitModule,
    AirPathNotesComponent,
    AlarmDeviceNotesComponent,
    CableRouteNotesComponent,
    FloorplanItemDetailsComponent,
    GasWarningCenterNotesComponent,
    ImageNotesComponent,
    MeasurementLineNotesComponent,
    PipelineNotesComponent,
    PlaceholderNotesComponent,
    PlasticSignNotesComponent,
    ProtectiveTubeNotesComponent,
    ReferenceMeasureNotesComponent,
    SignalNotesComponent,
    TransmitterNotesComponent,
    WireNotesComponent,
    ZoneNotesComponent,
    GroupIdChipComponent,
    PatientMonitorNotesComponent,
    CentralStationNotesComponent,
    TelemetryNotesComponent,
    KvmNotesComponent,
    DisplayNotesComponent,
    UtilityNotesComponent,
    FlameDetectorNotesComponent,
    FreeTextNotesComponent,
  ],
  standalone: true,
})
export class FloorplanAdditionalNotesComponent implements OnInit {
  private readonly floorplanService = inject(FloorplanService);
  private readonly productService = inject(ProductService);
  private readonly utilityProductService = inject(UtilityProductService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly projectImageService = inject(ProjectImageService);

  protected readonly title = signal<string | null>(null);
  protected readonly itemType = signal<FloorplanSelectedItemType>(FloorplanSelectedItemType.None);
  protected readonly selectedItem = signal<FloorplanItem | null>(null);
  protected readonly itemSelectIntention = signal<ItemSelectIntention | null>(null);
  protected readonly ItemType = FloorplanSelectedItemType;
  protected readonly SidesheetExtent = FloorplanSidesheetExtent;
  protected readonly selectedItemIsProductOrPlaceholder = computed(() => {
    const itemType = this.itemType();
    return (
      itemType === FloorplanSelectedItemType.AlarmDevice ||
      itemType === FloorplanSelectedItemType.GasWarningCenter ||
      itemType === FloorplanSelectedItemType.PlasticSign ||
      itemType === FloorplanSelectedItemType.SignalElement ||
      itemType === FloorplanSelectedItemType.Transmitter ||
      itemType === FloorplanSelectedItemType.FlameDetector ||
      itemType === FloorplanSelectedItemType.PatientMonitor ||
      itemType === FloorplanSelectedItemType.CentralStation ||
      itemType === FloorplanSelectedItemType.Telemetry ||
      itemType === FloorplanSelectedItemType.Kvm ||
      itemType === FloorplanSelectedItemType.Display ||
      itemType === FloorplanSelectedItemType.Utility ||
      itemType === FloorplanSelectedItemType.Placeholder
    );
  });
  protected readonly sidesheetExtent = signal(FloorplanSidesheetExtent.None);

  ngOnInit(): void {
    this.handleSelectedItemChange();
  }

  protected getGroup(item: FloorplanItem) {
    if (!this.selectedItemIsProductOrPlaceholder()) return null;
    const floorplan = this.floorplanService.selectedFloorplan;
    return (
      floorplan.project.groups.find((g) =>
        g.references.some((r) => r.floorplanId === floorplan.id && r.floorplanProductItemId === (item as ProductTypesInGroup).id),
      ) ?? null
    );
  }

  protected close() {
    this.floorplanService.deselectItem();
  }

  protected toggleExtended() {
    this.sidesheetExtent.update((currentExtent) => {
      if (currentExtent !== FloorplanSidesheetExtent.None) {
        return FloorplanSidesheetExtent.None;
      }
      if (
        this.selectedItem() instanceof FloorplanGasWarningCenter ||
        this.selectedItem() instanceof FloorplanGasWarningCenterPlaceholder
      ) {
        return FloorplanSidesheetExtent.Fully;
      }
      if (
        this.selectedItem() instanceof FloorplanTransmitter ||
        this.selectedItem() instanceof FloorplanTransmitterPlaceholder ||
        this.selectedItem() instanceof FloorplanFlameDetector ||
        this.selectedItem() instanceof FloorplanFlameDetectorPlaceholder ||
        this.selectedItem() instanceof FloorplanSignalElement ||
        this.selectedItem() instanceof FloorplanPlasticSign ||
        this.selectedItem() instanceof FloorplanAlarmDevice ||
        this.selectedItem() instanceof FloorplanSimplePlaceholder ||
        this.selectedItem() instanceof FloorplanPatientMonitor ||
        this.selectedItem() instanceof FloorplanCentralStation ||
        this.selectedItem() instanceof FloorplanKvm ||
        this.selectedItem() instanceof Pipeline ||
        this.selectedItem() instanceof CableRoute ||
        this.selectedItem() instanceof Wire ||
        this.selectedItem() instanceof FloorplanPatientMonitor ||
        this.selectedItem() instanceof FloorplanCentralStation ||
        this.selectedItem() instanceof FloorplanTelemetry ||
        this.selectedItem() instanceof FloorplanDisplay ||
        this.selectedItem() instanceof FloorplanUtility
      ) {
        return FloorplanSidesheetExtent.Partially;
      }
      return FloorplanSidesheetExtent.None;
    });
  }

  private updateItemTypeAndTitle(selectedItem: FloorplanItem | null): Observable<undefined> {
    if (!selectedItem) {
      this.itemType.set(FloorplanSelectedItemType.None);
      return of(undefined);
    }
    if (
      selectedItem instanceof FloorplanSimplePlaceholder ||
      selectedItem instanceof FloorplanTransmitterPlaceholder ||
      selectedItem instanceof FloorplanFlameDetectorPlaceholder ||
      selectedItem instanceof FloorplanGasWarningCenterPlaceholder
    ) {
      this.itemType.set(FloorplanSelectedItemType.Placeholder);
      this.title.set(this.getTitleForPlaceholder(selectedItem));
      return of(undefined);
    }
    if (selectedItem instanceof MeasurementLine) {
      this.itemType.set(FloorplanSelectedItemType.MeasurementLine);
      this.title.set($localize`:@@floorplan.details.measurement.header:Bemaßung`);
      return of(undefined);
    }
    if (selectedItem instanceof ReferenceMeasure) {
      this.itemType.set(FloorplanSelectedItemType.ReferenceMeasure);
      this.title.set($localize`:@@floorplan.details.referenceMeasure.header:Referenzmaß`);
      return of(undefined);
    }
    if (selectedItem instanceof FloorplanImage) {
      this.itemType.set(FloorplanSelectedItemType.Image);
      return this.projectImageService.getImageMetadataFromIndexedDB(selectedItem.projectImageId).pipe(
        tap((image) => this.title.set(image?.name ?? "")),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanTransmitter) {
      this.itemType.set(FloorplanSelectedItemType.Transmitter);
      return this.resolveProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanFlameDetector) {
      this.itemType.set(FloorplanSelectedItemType.FlameDetector);
      return this.resolveProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanGasWarningCenter) {
      this.itemType.set(FloorplanSelectedItemType.GasWarningCenter);
      if (selectedItem.config.regard3000Configuration) {
        if (selectedItem.config.name.toLowerCase().includes("plus")) {
          this.title.set($localize`:@@gasWarningCenterConfiguration.regard3000Plus.displayName:REGARD 3000 Plus`);
          return of(undefined);
        }
        this.title.set($localize`:@@gasWarningCenterConfiguration.regard3000Base.displayName:REGARD 3000`);
        return of(undefined);
      } else if (selectedItem.config.regard7000PlusConfiguration) {
        this.title.set($localize`:@@gasWarningCenterConfiguration.regard7000.displayName:REGARD 7000`);
        return of(undefined);
      }
      return this.resolveProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanSignalElement) {
      this.itemType.set(FloorplanSelectedItemType.SignalElement);
      return this.resolveProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanAlarmDevice) {
      this.itemType.set(FloorplanSelectedItemType.AlarmDevice);
      return this.resolveAlarmDeviceName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanPlasticSign) {
      this.itemType.set(FloorplanSelectedItemType.PlasticSign);
      return this.resolveProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof Pipeline) {
      this.itemType.set(FloorplanSelectedItemType.Pipeline);
      this.title.set(selectedItem.name || $localize`:@@floorplan.details.pipeline.header:Rohrleitung`);
      return of(undefined);
    }
    if (selectedItem instanceof CableRoute) {
      this.itemType.set(FloorplanSelectedItemType.CableRoute);
      this.title.set(selectedItem.name || $localize`:@@floorplan.details.cableRoute.header:Kabeltrasse`);
      return of(undefined);
    }
    if (selectedItem instanceof ProtectiveTube) {
      this.itemType.set(FloorplanSelectedItemType.ProtectiveTube);
      this.title.set(selectedItem.name || $localize`:@@floorplan.details.protectiveTube.header:Schutzrohr`);
      return of(undefined);
    }
    if (selectedItem instanceof Wire) {
      this.itemType.set(FloorplanSelectedItemType.Wire);
      this.title.set(selectedItem.name || $localize`:@@floorplan.details.wire.header:Leitung`);
      return of(undefined);
    }
    if (
      selectedItem instanceof DangerArea ||
      selectedItem instanceof ExZone ||
      selectedItem instanceof DustExZone ||
      selectedItem instanceof WildcardZone ||
      selectedItem instanceof IsolationRoom ||
      selectedItem instanceof ItRoom ||
      selectedItem instanceof OperatingTheatre ||
      selectedItem instanceof ProcedureRoom ||
      selectedItem instanceof StandardRoom
    ) {
      this.itemType.set(FloorplanSelectedItemType.Zone);
      this.title.set(this.getTitleForZone(selectedItem));
      return of(undefined);
    }
    if (selectedItem instanceof AirPath) {
      this.itemType.set(FloorplanSelectedItemType.AirPath);
      this.title.set(
        selectedItem.direction == "EXHAUST_AIR"
          ? $localize`:@@floorplan.details.exhaustAir.header:Abluftweg`
          : $localize`:@@floorplan.details.supplyAir.header:Zuluftweg`,
      );
      return of(undefined);
    }
    if (selectedItem instanceof FloorplanPatientMonitor) {
      this.itemType.set(FloorplanSelectedItemType.PatientMonitor);
      return this.resolveMonitoringProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanCentralStation) {
      this.itemType.set(FloorplanSelectedItemType.CentralStation);
      return this.resolveMonitoringProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanTelemetry) {
      this.itemType.set(FloorplanSelectedItemType.Telemetry);
      return this.resolveMonitoringProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanKvm) {
      this.itemType.set(FloorplanSelectedItemType.Kvm);
      return this.resolveMonitoringProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanDisplay) {
      this.itemType.set(FloorplanSelectedItemType.Display);
      return this.resolveMonitoringProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanUtility) {
      this.itemType.set(FloorplanSelectedItemType.Utility);
      return this.resolveMonitoringProductName(selectedItem).pipe(
        tap((name) => this.title.set(name)),
        map(() => undefined),
      );
    }
    if (selectedItem instanceof FloorplanFreeText) {
      this.itemType.set(FloorplanSelectedItemType.FreeText);
      this.title.set($localize`:@@floorplan.details.freeText.header:Freitext`);
      return of(undefined);
    }
    this.itemType.set(FloorplanSelectedItemType.Default);
    if (selectedItem instanceof FloorplanText) {
      this.title.set($localize`:@@floorplan.details.notes.header:Notiz`);
      return of(undefined);
    }
    this.title.set(null);
    return of(undefined);
  }

  private handleSelectedItemChange() {
    this.floorplanService.selectedItem$
      .pipe(
        map((selectedItem) => {
          const isMultiSelectMode =
            this.floorplanService.selectedFloorplan.floorplanState.selectionMode === FloorplanSelectionMode.MULTI;
          return isMultiSelectMode ? { item: null, selectIntention: null } : selectedItem;
        }),
        tap((selectedItem) => {
          this.sidesheetExtent.set(FloorplanSidesheetExtent.None);
          this.itemSelectIntention.set(selectedItem.selectIntention);
          this.selectedItem.set(selectedItem.item);
        }),
        switchMap((selectedItem) => this.updateItemTypeAndTitle(selectedItem.item).pipe(map(() => selectedItem))),
        tap((selectedItem) => {
          if (selectedItem.selectIntention === ItemSelectIntention.EXTENDED_VIEW) {
            this.toggleExtended();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.changeDetectorRef.detectChanges());
  }

  private getTitleForPlaceholder(
    floorplanItem:
      | FloorplanSimplePlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetectorPlaceholder
      | FloorplanGasWarningCenterPlaceholder,
  ): string {
    if (floorplanItem instanceof FloorplanTransmitterPlaceholder) {
      return $localize`:@@floorplan.details.placeholder.transmitter.header:Platzhalter (Transmitter)`;
    }

    if (floorplanItem instanceof FloorplanFlameDetectorPlaceholder) {
      return $localize`:@@floorplan.details.placeholder.flameDetector.header:Platzhalter (Brandmelder)`;
    }
    if (floorplanItem instanceof FloorplanGasWarningCenterPlaceholder) {
      return $localize`:@@floorplan.details.placeholder.gasWarningCenter.header:Platzhalter (Gaswarnzentrale)`;
    }
    if ((floorplanItem as FloorplanSimplePlaceholder).type === "alarmDevice") {
      return $localize`:@@floorplan.details.placeholder.alarmDevice.header:Platzhalter (Alarmmittel)`;
    }
    if ((floorplanItem as FloorplanSimplePlaceholder).type === "plasticSign") {
      return $localize`:@@floorplan.details.placeholder.plasticSign.header:Platzhalter (Kunststoffschild)`;
    }
    if ((floorplanItem as FloorplanSimplePlaceholder).type === "signalElement") {
      return $localize`:@@floorplan.details.placeholder.signalElement.header:Platzhalter (Leuchttransparent)`;
    }
    if ((floorplanItem as FloorplanSimplePlaceholder).type === "patientMonitor") {
      return $localize`:@@floorplan.details.placeholder.patientMonitor.header:Placeholder (Patient Monitor)`;
    }
    if ((floorplanItem as FloorplanSimplePlaceholder).type === "centralStation") {
      return $localize`:@@floorplan.details.placeholder.centralStation.header:Placeholder (Central Station)`;
    }
    if ((floorplanItem as FloorplanSimplePlaceholder).type === "telemetry") {
      return $localize`:@@floorplan.details.placeholder.telemetry.header:Placeholder (Telemetry)`;
    }
    if ((floorplanItem as FloorplanSimplePlaceholder).type === "kvm") {
      return $localize`:@@floorplan.details.placeholder.kvm.header:Placeholder (KVM)`;
    }
    if ((floorplanItem as FloorplanSimplePlaceholder).type === "display") {
      return $localize`:@@floorplan.details.placeholder.display.header:Placeholder (Display)`;
    }
    if ((floorplanItem as FloorplanSimplePlaceholder).type === "utility") {
      return $localize`:@@floorplan.details.placeholder.utility.header:Placeholder (Utility)`;
    }
    return "Platzhalter";
  }

  private getTitleForZone(
    floorplanItem:
      DangerArea | ExZone | DustExZone | WildcardZone | IsolationRoom | ItRoom | OperatingTheatre | ProcedureRoom | StandardRoom,
  ): string {
    if (floorplanItem instanceof DangerArea) {
      return $localize`:@@floorplan.details.dangerArea.header:Gefahrenbereich`;
    }
    if (floorplanItem instanceof ExZone) {
      const exZoneType = floorplanItem.exZoneType.charAt(floorplanItem.exZoneType.length - 1);
      return $localize`:@@floorplan.details.exZone.header:Ex-Zone ${exZoneType}:exZoneType:`;
    }
    if (floorplanItem instanceof WildcardZone) {
      const wildcardZoneType = floorplanItem.type.charAt(floorplanItem.type.length - 1);
      return $localize`:@@floorplan.details.wildcardZone.header:Variable Zone ${wildcardZoneType}:wildcardZoneType:`;
    }
    if (floorplanItem instanceof IsolationRoom) {
      return $localize`:@@floorplan.monitoring.details.isolationRoom.header:Isolation room`;
    }
    if (floorplanItem instanceof ItRoom) {
      return $localize`:@@floorplan.monitoring.details.itRoom.header:It room`;
    }
    if (floorplanItem instanceof OperatingTheatre) {
      return $localize`:@@floorplan.monitoring.details.operatingTheatre.header:Operating theatre`;
    }
    if (floorplanItem instanceof ProcedureRoom) {
      return $localize`:@@floorplan.monitoring.details.procedureRoom.header:Procedure room`;
    }
    if (floorplanItem instanceof StandardRoom) {
      return $localize`:@@floorplan.monitoring.details.standardRoom.header:Standard room`;
    }
    const dustExZoneType = floorplanItem.dustExZoneType.split("_")[1];
    return $localize`:@@floorplan.details.dustExZone.header:Staub-Ex-Zone ${dustExZoneType}:dustExZoneType:`;
  }

  private resolveProductName(
    floorplanProductItem:
      FloorplanGasWarningCenter | FloorplanTransmitter | FloorplanFlameDetector | FloorplanSignalElement | FloorplanPlasticSign,
  ): Observable<string> {
    const productId =
      floorplanProductItem instanceof FloorplanFlameDetector
        ? floorplanProductItem.config.variantId
        : floorplanProductItem.config.productId;
    return this.productService.getProductNameById(productId).pipe(map((name) => name ?? floorplanProductItem.config.name));
  }

  private resolveMonitoringProductName(
    floorplanProductItem:
      FloorplanPatientMonitor | FloorplanCentralStation | FloorplanTelemetry | FloorplanKvm | FloorplanDisplay | FloorplanUtility,
  ): Observable<string> {
    if (floorplanProductItem instanceof FloorplanDisplay) {
      return of(floorplanProductItem.config.display.option.groupName || floorplanProductItem.config.name);
    }
    if (floorplanProductItem instanceof FloorplanUtility) {
      return this.utilityProductService.resolveName(floorplanProductItem.config).pipe(take(1));
    }
    if (floorplanProductItem instanceof FloorplanKvm) {
      return of(floorplanProductItem.config.name);
    }
    if (floorplanProductItem instanceof FloorplanTelemetry) {
      return of(floorplanProductItem.config.family?.[0]?.option.groupName || floorplanProductItem.config.name);
    }
    return of(floorplanProductItem.config.family?.option.groupName || floorplanProductItem.config.name);
  }

  private resolveAlarmDeviceName(floorplanAlarmDevice: FloorplanAlarmDevice): Observable<string> {
    if (floorplanAlarmDevice.config.productIds.length > 1) {
      return of(
        $localize`:@@alarmDeviceConfiguration.combination.name:Alarm-Kombi (${floorplanAlarmDevice.config.productIds.join(", ")}:listOfAlarmDeviceIds:)`,
      );
    }
    return this.productService
      .getProductNameById(floorplanAlarmDevice.config.productIds[0])
      .pipe(map((name) => name ?? floorplanAlarmDevice.config.name));
  }
}
